import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Building2, FileArchive, Upload, Download, Trash2 } from "lucide-react";

const CAMPOS = [
  ["razao_social", "Razão Social"], ["nome_fantasia", "Nome Fantasia"], ["cnpj", "CNPJ"],
  ["inscricao_estadual", "Inscrição Estadual"], ["inscricao_municipal", "Inscrição Municipal"],
  ["cep", "CEP"], ["logradouro", "Logradouro"], ["numero", "Número"], ["complemento", "Complemento"],
  ["bairro", "Bairro"], ["cidade", "Cidade"], ["uf", "UF"],
  ["telefone", "Telefone"], ["whatsapp", "WhatsApp"], ["email_geral", "E-mail geral"],
  ["email_contratual", "E-mail contratual"], ["email_financeiro", "E-mail financeiro"],
  ["email_privacidade", "E-mail privacidade/LGPD"], ["site", "Site"],
  ["representante_institucional", "Representante institucional"], ["cargo_representante", "Cargo"],
];

const GRUPOS = [
  { titulo: "Catálogos", itens: ["Nichos", "Serviços", "Planos", "Faixas de cobrança"] },
  { titulo: "Operação", itens: ["Score (pesos)", "SLA", "Feriados", "Mensagens automáticas"] },
];

export default function Configuracoes() {
  const [form, setForm] = useState({});
  const [docs, setDocs] = useState({ tipos: {}, documentos: [] });
  const [novoDoc, setNovoDoc] = useState({ tipo: "", nome: "", arquivo: null });
  const [logo, setLogo] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/admin/plataforma/institucional").then(r => setForm(r.data)).catch(() => {});
    api.get("/admin/plataforma/documentos").then(r => setDocs(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const salvar = async () => {
    setBusy(true);
    try {
      await api.put("/admin/plataforma/institucional", form);
      if (logo) {
        const fd = new FormData(); fd.append("arquivo", logo);
        await api.post("/admin/plataforma/logotipo", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setLogo(null);
      }
      toast.success("Informações institucionais salvas — os próximos contratos usarão estes dados");
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const enviarDoc = async () => {
    if (!novoDoc.tipo || !novoDoc.nome.trim() || !novoDoc.arquivo) {
      toast.error("Informe tipo, nome e arquivo"); return;
    }
    const fd = new FormData();
    fd.append("tipo", novoDoc.tipo); fd.append("nome", novoDoc.nome); fd.append("arquivo", novoDoc.arquivo);
    setBusy(true);
    try {
      await api.post("/admin/plataforma/documentos", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Documento arquivado (interno, só ADM)");
      setNovoDoc({ tipo: "", nome: "", arquivo: null });
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const baixar = async (d) => {
    try {
      const r = await api.get(`/admin/plataforma/documentos/${d.id}/arquivo`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = d.filename || d.nome; a.click();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const excluir = async (d) => {
    if (!window.confirm(`Excluir "${d.nome}"?`)) return;
    try { await api.delete(`/admin/plataforma/documentos/${d.id}`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="p-8">
      <div className="mb-6">
        <div className="label-cap">Plataforma · MontaJá</div>
        <h1 className="font-heading font-extrabold text-3xl tracking-tighter mt-1">Configurações</h1>
      </div>

      <div className="border border-border bg-white p-5" data-testid="cfg-institucional">
        <div className="label-cap mb-1"><Building2 size={13} className="inline mr-1" />Informações Institucionais da Plataforma</div>
        <p className="text-xs text-muted-foreground mb-4">
          Estes dados são puxados automaticamente para os contratos Empresa × MontaJá. Contratos já assinados
          nunca mudam retroativamente (snapshot no momento do aceite).
        </p>
        <div className="grid sm:grid-cols-3 gap-3">
          {CAMPOS.map(([k, l]) => (
            <div key={k}>
              <Label className="label-cap">{l}</Label>
              <Input value={form[k] || ""} onChange={e => setForm(f => ({ ...f, [k]: e.target.value }))}
                className="mt-1" data-testid={`inst-${k}`} />
            </div>
          ))}
          <div>
            <Label className="label-cap">Logotipo</Label>
            <input type="file" accept=".png,.jpg,.jpeg,.webp,.svg" className="mt-2 text-xs block"
              onChange={e => setLogo(e.target.files?.[0] || null)} data-testid="inst-logotipo" />
            {form.logotipo_ext && <span className="text-[10px] text-emerald-600">✓ logotipo atual: .{form.logotipo_ext}</span>}
          </div>
        </div>
        <Button onClick={salvar} disabled={busy} className="btn-sharp brand-bg text-white mt-4" data-testid="inst-salvar">
          Salvar informações institucionais
        </Button>
      </div>

      <div className="border border-border bg-white p-5 mt-4" data-testid="cfg-documentos">
        <div className="label-cap mb-1"><FileArchive size={13} className="inline mr-1" />Documentos Institucionais (interno)</div>
        <p className="text-xs text-muted-foreground mb-3">
          Arquivo interno da plataforma — não aparece para Empresas, Montadoras, Montadores nem Clientes.
          Use para guardar e, se necessário, enviar manualmente a futuras empresas.
        </p>
        <div className="grid sm:grid-cols-4 gap-2 items-end">
          <div>
            <Label className="label-cap">Tipo</Label>
            <select value={novoDoc.tipo} onChange={e => setNovoDoc(d => ({ ...d, tipo: e.target.value }))}
              className="mt-1 w-full h-9 border border-border px-2 text-sm bg-white" data-testid="doc-tipo">
              <option value="">Selecione…</option>
              {Object.entries(docs.tipos).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          <div>
            <Label className="label-cap">Nome</Label>
            <Input value={novoDoc.nome} onChange={e => setNovoDoc(d => ({ ...d, nome: e.target.value }))}
              className="mt-1" data-testid="doc-nome" />
          </div>
          <input type="file" className="text-xs" onChange={e => setNovoDoc(d => ({ ...d, arquivo: e.target.files?.[0] || null }))}
            data-testid="doc-arquivo" />
          <Button onClick={enviarDoc} disabled={busy} variant="outline" className="btn-sharp" data-testid="doc-enviar">
            <Upload size={14} /> Arquivar
          </Button>
        </div>
        <div className="mt-4 space-y-1">
          {docs.documentos.length === 0 && <p className="text-xs text-muted-foreground">Nenhum documento arquivado.</p>}
          {docs.documentos.map(d => (
            <div key={d.id} className="flex items-center justify-between py-1.5 border-b border-border/60 text-xs"
              data-testid={`doc-item-${d.id}`}>
              <span><strong>{d.nome}</strong> · {docs.tipos[d.tipo] || d.tipo} · {d.filename}</span>
              <span className="flex gap-1">
                <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => baixar(d)} data-testid={`doc-baixar-${d.id}`}>
                  <Download size={13} />
                </Button>
                <Button size="sm" variant="ghost" className="h-7 px-2 text-red-600" onClick={() => excluir(d)} data-testid={`doc-excluir-${d.id}`}>
                  <Trash2 size={13} />
                </Button>
              </span>
            </div>
          ))}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4 mt-4">
        {GRUPOS.map(g => (
          <div key={g.titulo} className="border border-border bg-white p-5" data-testid={`cfg-${g.titulo.toLowerCase()}`}>
            <div className="label-cap mb-3">{g.titulo}</div>
            <ul className="space-y-2">
              {g.itens.map(item => (
                <li key={item} className="flex justify-between items-center py-2 border-b border-border/50 last:border-0">
                  <span className="text-sm">{item}</span>
                  <span className="text-[10px] label-cap text-muted-foreground/60">Em breve</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}
